import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowCounterClockwise, Check, X } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { useApp } from '@/contexts/AppContext'
import { getOpenQuestionCount, isThreadOpen } from '@/contexts/AppContext'
import { useDebounce } from '@/hooks/useDebounce'
import { cn } from '@/lib/utils'
import { ArrowLeftIcon } from 'lucide-react'

export function ArchivioPage() {
  const { state, dispatch } = useApp()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  
  const debouncedSearchQuery = useDebounce(searchQuery, 150)
  
  // Filter to show only archived entities
  const archivedEntities = Object.values(state.entities).filter(entity => entity.isArchived)
  
  // Apply search filter
  const filteredEntities = archivedEntities.filter(entity =>
    entity.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
  )

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="lg" className="h-10 w-10 p-0" aria-label={t('back')} onClick={() => {
          if (window.history.length > 1) { navigate(-1) } else { navigate('/', { replace: true }) }
        }}>
          <ArrowLeftIcon className="size-7" />
        </Button>
        <h1 className="text-lg font-semibold">{t('archivio')}</h1>
      </div>

      {/* Search Bar */}
      <div className="mb-4">
        <Input
          type="text"
          placeholder={t('searchEntities')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="max-w-md"
        />
      </div>

      {/* Archived Entities List */}
      {filteredEntities.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <p>{archivedEntities.length === 0 ? t('noArchivedEntities') : 'No archived entities match your search.'}</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filteredEntities.map((entity) => {
            const threadCount = entity.threadIds.length
            const openQuestionCount = getOpenQuestionCount(entity.id, state)

            const hasOpenThread = state.entities[entity.id].threadIds.some((tid) => {
              const t = state.threads[tid]
              if (!t) return false
              return isThreadOpen(t.rootQuestionId, state.qaNodes)
            })
            return (
              <ArchivedEntityListItem
                key={entity.id}
                entityId={entity.id}
                name={entity.name}
                hasOpenThread={hasOpenThread}
                threadCount={threadCount}
                openQuestionCount={openQuestionCount}
                createdAt={entity.createdAt}
                archivedAt={entity.archivedAt}
              />
            )}
          )}
        </div>
      )}
    </div>
  )
}

interface ArchivedEntityListItemProps {
  entityId: string
  name: string
  hasOpenThread: boolean
  threadCount: number
  openQuestionCount: number
  createdAt: string
  archivedAt?: string | null
}

function ArchivedEntityListItem({ entityId, name, hasOpenThread, threadCount, openQuestionCount, createdAt, archivedAt }: ArchivedEntityListItemProps) {
  const { t } = useTranslation()
  const { state, dispatch } = useApp()
  const verboseMode = state.settings?.verboseMode ?? true

  const handleRestore = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dispatch({ type: 'UNARCHIVE_ENTITY', payload: { id: entityId } } as any)
  }

  const createdDate = new Date(createdAt).toLocaleDateString()
  const archivedDate = archivedAt ? new Date(archivedAt).toLocaleDateString() : ''

  return (
    <Link to={`/archivio/${entityId}`}>
      <Card className={cn(
        "p-4 transition-colors cursor-pointer border-slate-200",
        hasOpenThread ? "bg-yellow-50 hover:bg-yellow-100" : "bg-slate-50 hover:bg-slate-100"
      )}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1">
            <div className="w-fit">
              <h3 className="font-medium text-sm">
                {name}
              </h3>
              <div className="text-xs text-muted-foreground mt-1 space-y-1">
                <div>{t('createdAt')}: {createdDate}</div>
                {archivedDate && <div>{t('archivedAt')}: {archivedDate}</div>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 ml-4">
            <div className="text-right">
              <div className="text-xs text-muted-foreground">{t('threads')}</div>
              <div className="text-sm font-medium">{threadCount}</div>
            </div>
            {verboseMode && openQuestionCount > 0 && (
              <div className="text-right">
                <div className="text-xs text-muted-foreground">{t('openQuestions')}</div>
                <Badge variant="secondary" className="text-xs">
                  {openQuestionCount}
                </Badge>
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleRestore}
              className="text-xs"
            >
              <ArrowCounterClockwise size={12} className="mr-1" />
              {t('ripristina')}
            </Button>
          </div>
        </div>
      </Card>
    </Link>
  )
}